export const CATALOG_COLUMN_COUNT = 3;

/** Initial equal grid from flat row-major API list (index 1,2,3…). */
export function splitIntoColumns(items, columnCount = CATALOG_COLUMN_COUNT) {
  const columns = Array.from({ length: columnCount }, () => []);
  items.forEach((item, index) => {
    columns[index % columnCount].push({ item, index });
  });
  return columns;
}

/** Flatten columns row-by-row (only filled cells) for API index order. */
export function columnsToFlat(columns) {
  const columnCount = columns.length;
  const maxLen = Math.max(0, ...columns.map((col) => col.length));
  const result = [];
  for (let row = 0; row < maxLen; row += 1) {
    for (let col = 0; col < columnCount; col += 1) {
      if (columns[col][row]) result.push(columns[col][row]);
    }
  }
  return result;
}

const COLUMN_LAYOUT_STORAGE_KEY = "mr-phone-pricing-catalog-column-layout";

export const saveColumnLayout = (columns) => {
  try {
    const themes = {};
    columns.forEach((col) => {
      col.forEach((group) => {
        if (group?.id != null && group.themeKey) {
          themes[group.id] = group.themeKey;
        }
      });
    });
    localStorage.setItem(
      COLUMN_LAYOUT_STORAGE_KEY,
      JSON.stringify({
        columns: columns.map((col) => col.map((g) => g.id)),
        themes,
      })
    );
  } catch {
    // ignore quota / private mode
  }
};

export const readColumnLayout = () => {
  try {
    const raw = localStorage.getItem(COLUMN_LAYOUT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return { columns: parsed, themes: null };
    }
    if (parsed && Array.isArray(parsed.columns)) {
      return {
        columns: parsed.columns,
        themes: parsed.themes && typeof parsed.themes === "object" ? parsed.themes : null,
      };
    }
    return null;
  } catch {
    return null;
  }
};

/** Build column stacks from stored ids; append new groups to the shortest column. */
export function applyStoredColumnLayout(
  groups,
  storedColumnIds,
  themeById = {},
  columnCount = CATALOG_COLUMN_COUNT
) {
  const byId = new Map(groups.map((g) => [g.id, g]));
  const used = new Set();
  const columns = Array.from({ length: columnCount }, () => []);

  const withTheme = (group) => {
    const themeKey = themeById[group.id] ?? group.themeKey;
    return themeKey ? { ...group, themeKey } : group;
  };

  (storedColumnIds ?? []).forEach((colIds, col) => {
    if (col >= columnCount) return;
    colIds.forEach((id) => {
      const group = byId.get(id);
      if (!group) return;
      used.add(id);
      columns[col].push(withTheme(group));
    });
  });

  groups.forEach((group) => {
    if (used.has(group.id)) return;
    const shortest = columns.reduce(
      (minIdx, col, i, arr) => (col.length < arr[minIdx].length ? i : minIdx),
      0
    );
    columns[shortest].push(withTheme(group));
  });

  return columns;
}

/** First load: round-robin by sorted index (equal masonry grid). */
export function groupsToColumns(groups, columnCount = CATALOG_COLUMN_COUNT) {
  const columns = Array.from({ length: columnCount }, () => []);
  groups.forEach((group, index) => {
    columns[index % columnCount].push(group);
  });
  return columns;
}

const cloneColumns = (columns) => columns.map((col) => [...col]);

const findGroupColumnIndex = (columns, groupId) => {
  for (let col = 0; col < columns.length; col += 1) {
    const index = columns[col].findIndex((g) => g.id === groupId);
    if (index >= 0) return { col, index };
  }
  return null;
};

export const columnsOrderKey = (columns) =>
  columns.map((col) => col.map((g) => g.id).join("-")).join("|");

/**
 * Insert dragged group at target slot inside its column; shift down in that column only.
 * Returns new column arrays — no items jump to other columns.
 */
export function insertGroupInColumns(columns, srcId, tgtId) {
  const cols = cloneColumns(columns);
  const src = findGroupColumnIndex(cols, srcId);
  const tgt = findGroupColumnIndex(cols, tgtId);
  if (!src || !tgt || srcId === tgtId) return columns;

  const [moved] = cols[src.col].splice(src.index, 1);
  let insertAt = tgt.index;
  if (src.col === tgt.col && src.index < insertAt) insertAt -= 1;

  cols[tgt.col].splice(insertAt, 0, moved);
  return cols;
}
